import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppConfigs, AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';

interface SortableItemProps {
    option: any;
}

const SortableItem = ({ option }: SortableItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: option.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: { xs: 36, md: 82 },
                px: { xs: 3, md: 4.5 },
                borderRadius: '24px',
                border: '4px solid #d2d9e1',
                backgroundColor: '#ffffff',
                cursor: 'grab',
                transition: 'all 0.2s ease',
                boxShadow: isDragging ? '0 10px 24px rgba(0,0,0,0.12)' : 'none',
                opacity: isDragging ? 0.95 : 1,
                '&:hover': {
                    borderColor: '#bfc9d4',
                    transform: 'translateY(-1px)',
                },
                '&:active': {
                    cursor: 'grabbing',
                },
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: 14, md: 18 },
                    fontWeight: 700,
                    color: '#1e2f46',
                    lineHeight: 1.2,
                }}
            >
                {option.name}
            </Typography>
        </Box>
    );
};

const calculateValueForIndex = (index: number, totalCount: number) => {
    if (totalCount <= 1) {
        return 100;
    }

    const max = 100;
    const min = 10;

    const step = (max - min) / (totalCount - 1);

    return Math.round(max - (index * step));
};

const CustomSelectSetting3 = ({
    id,
    items,
    onTradeoffValueChange,
    title,
    description,
}: {
    disabled?: boolean;
    importance: number;
    items: {
        name: string;
        value: number | string;
        icon: string;
        link: string;
    }[];
    hideValues?: boolean;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange']
    initialValues: Record<string, string>;
    id: string;
    criteriaConstraints: Record<string, CriteriaConstraint[]>;
    setCriteriaConstraints: (value: (((prevState: { [p: string]: CriteriaConstraint[]; }) => {
        [p: string]: CriteriaConstraint[];
    }) | { [p: string]: CriteriaConstraint[]; })) => void;
    variant?: AppConfigs['criteriaSettings']['enumerated'];
    isModal?: boolean;
    onCriteriaSettingsOpen?: (id: string) => void;
    title: string;
    description: string;
}) => {
    const [ options, setOptions ] = useState<any[]>([]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        setOptions((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);

            const newOptions = arrayMove(items, oldIndex, newIndex);
            const totalCount = newOptions.length;
            newOptions.forEach((item, idx) => {
                onTradeoffValueChange(calculateValueForIndex(idx, totalCount), id, item.name);
            });
            return newOptions;
        });
    };

    useEffect(() => {
        const tmpItems = items.map(item => ({
            id: `id-${item.name}`,
            ...item,
        }));
        tmpItems.sort((a, b) => +a.value - +b.value);
        setOptions(tmpItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 1500,
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: 28, md: 38 },
                    lineHeight: 1.15,
                    fontWeight: 700,
                    color: '#1e2f46',
                    mb: 3,
                }}
            >
                {title}
            </Typography>

            <Typography
                sx={{
                    fontSize: { xs: 12, md: 16 },
                    lineHeight: 1.5,
                    color: '#667892',
                    mb: 5,
                    maxWidth: 1200,
                }}
            >
                {description}
            </Typography>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={options.map((option) => option.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <Stack spacing={2}>
                        {options.map((option) => (
                            <SortableItem key={option.id} option={option}/>
                        ))}
                    </Stack>
                </SortableContext>
            </DndContext>
        </Box>
    );
};

export default CustomSelectSetting3;
